import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  amountFormatter,
  getAllAuctionEvents,
  getAuctionEventById,
} from "services";
import CustomBasicDataTable from "../../../../shadcn/components/custom/tables/CustomBasicDataTable";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../../../../shadcn/components/ui/button";
import { DateTime } from "luxon";
import { useRouter } from "next/router";
import PropertyDetailsModel from "./PropertyDetailsModel";
import { Calendar } from "../../../../shadcn/components/ui/calendar";
import CalendarDotsIcon from "../../../../icons/CalendarDotsIcon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../shadcn/components/ui/popover";
import ChevronUpSvg from "../../../../icons/ChevronUpSvg";
import ChevronDownSvg from "../../../../icons/ChevronDownSvg";
import { useSelector } from "react-redux";
import Pusher from "pusher-js";
import AuctionLoader from "../auctionFloor/AuctionLoader";
import OnboardingWarningBanner from "../onboarding/OnboardingWarningBanner";

export type tableData = {
  [x: string]: any;
  id: string;
};

export default function ViewAllEventsWrapper() {
  const [events, setEvents] = useState<any[]>([]);
  const eventsRef = useRef<any[]>(events);
  const divRef = useRef<HTMLDivElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [width, setWidth] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  const [search, setSearch] = useState("");
  const router = useRouter();
  const portalAccessType = useSelector((state: any) => {
    return state.bidderAuth.portalAccessType;
  });
  const onlySwissEventUser = portalAccessType === "swiss";

  const handleSetOpen = useCallback(
    (value: boolean) => {
      setOpen(value);
      if (!value) {
        router.replace("/all-events", undefined, { shallow: true });
      }
    },
    [router],
  );

  const onboardingData = useSelector((state: any) => {
    return state.bidderAuth.onboarding;
  });

  // Fetch data when filters or page change
  useEffect(() => {
    init(page, search);
  }, [page, search, startDate, endDate]);

  // Initial fetch on mount
  useEffect(() => {
    init(1, "");
  }, []);

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  const hasHandledDeepLinkRef = useRef(false);
  useEffect(() => {
    if (!router?.isReady) return;
    if (hasHandledDeepLinkRef?.current) return;
    if (loading) return;

    const { property_listing_id, view_detail } = router?.query;
    if (
      typeof property_listing_id !== "string" ||
      property_listing_id.trim() === "" ||
      view_detail !== "true"
    ) {
      return;
    }

    hasHandledDeepLinkRef.current = true;

    const local = events?.find((e) => e?.listing_id === property_listing_id);
    if (local) {
      viewDetailsClickHandler(local);
      return;
    }

    let aborted = false;
    (async () => {
      try {
        const event: any = await getAuctionEventById(property_listing_id);
        if (aborted) return;
        if (!event || event?.listing_id !== property_listing_id) return;
        const normalized = {
          ...event,
          bank_name: event?.bank?.name,
          bid_application_form_file_id:
            event?.bank?.bid_application_form_file_id?.uuid || null,
          online_kyc_enabled: event?.bank?.online_kyc_enabled || false,
        };
        viewDetailsClickHandler(normalized);
      } catch (err) {
        console.log("deep-link event fetch failed:", err);
      }
    })();

    return () => {
      aborted = true;
    };
  }, [router?.isReady, router?.query, loading]);

  const init = async (currentPage: number, searchTerm?: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setLoading(true);
    try {
      if (onlySwissEventUser !== true) {
        const result: any = await getAllAuctionEvents(
          ["accepted"],
          startDate
            ? DateTime.fromJSDate(new Date(startDate))
                .setZone("Asia/Kolkata")
                .toUTC()
                .toISO() || null
            : null,
          endDate
            ? DateTime.fromJSDate(new Date(endDate))
                .setZone("Asia/Kolkata")
                .set({ hour: 23, minute: 59, second: 0, millisecond: 0 })
                .toUTC()
                .toISO() || null
            : null,
          searchTerm || null, // Pass search to backend for server-side filtering
          currentPage,
        );
        const allEventsData = (result?.data || []).map((item: any) => ({
          ...item,
          bank_name: item.bank?.name,
          bid_application_form_file_id:
            item.bank?.bid_application_form_file_id?.uuid || null,
          online_kyc_enabled: item.bank?.online_kyc_enabled || false,
        }));
        setEvents(allEventsData);
        setTotalPages(result?.meta?.total_page || 1);
        setTotalEvents(result?.meta?.total || 0);
        setPage(currentPage);
        setLoading(false);
      } else {
        setEvents([]);
        setLoading(false);
      }
    } catch (error) {
      console.log("error: ", error);
      setLoading(false);
    }
  };

  const handleStartAuctionDataChange = (data: any) => {
    if (!data?.event_uuid || !data?.event_type) {
      return;
    }

    const eventUuid = data.event_uuid;
    const eventType = data.event_type;

    if (
      eventType === "auction_event_has_started" ||
      eventType === "auction_event_has_ended"
    ) {
      setEvents((prevEvents: any[]) => {
        const updatedEvents = prevEvents.filter(
          (event: any) => event.uuid !== eventUuid,
        );
        return updatedEvents;
      });
    }
  };

  const user = useSelector((state: any) => {
    return state.bidderAuth.user;
  });
  ///Pusher for updating the table when event changes
  useEffect(() => {
    Pusher.logToConsole = false;
    const pusher = new Pusher(`${process.env.NEXT_PUBLIC_PUSHER_KEY}`, {
      cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
    });

    const channelId = `update-dashboard`;
    const channel = pusher.subscribe(channelId);

    channel.bind("auction_event_has_started", (data: any) => {
      if (data) {
        console.log("data:started ", data);
        if (user) {
          handleStartAuctionDataChange(data);
        }
      }
    });

    channel.bind("auction_event_has_ended", (data: any) => {
      if (data) {
        console.log("data:ended ", data);
        if (user) {
          handleStartAuctionDataChange(data);
        }
      }
    });

    pusher.connection.bind("error", function (err: any) {
      if (err?.error?.data?.code === 4004) {
        console.log(">>> detected limit error");
      } else if (err?.data?.code === 1006) {
        // Websocket: CLOSED ABNORMALLY
      }
    });

    pusher.connection.bind("disconnected", function (err: any) {
      console.log(">>> disconnected");
    });

    pusher.connection.bind("connected", function (err: any) {
      console.log("connected", pusher.sessionID);
    });

    return function cleanup() {
      pusher.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const viewDetailsClickHandler = (propertyData: any) => {
    setSelected({
      ...propertyData.property,
      images: [
        {
          path_to_file:
            propertyData?.property_dump?.images?.length > 0
              ? propertyData?.property_dump?.images?.[0]?.path_to_file
              : propertyData?.property_dump?.sub_asset_type_images?.length > 0
              ? propertyData?.property_dump?.sub_asset_type_images?.[0]
                  ?.path_to_file
              : process.env.NEXT_PUBLIC_IMAGIX_URL?.includes("staging")
              ? `${".com/"}/27f785cc-24cb-4531-9b91-4a6c58bfaf37`
              : `${".com/"}/771263b2-a630-46f2-ae99-24cece88c4be`,
          uuid:
            propertyData?.property_dump?.images?.length > 0
              ? propertyData?.property_dump?.images?.[0]?.uuid
              : propertyData?.property_dump?.sub_asset_type_images?.length > 0
              ? propertyData?.property_dump?.sub_asset_type_images?.[0]?.uuid
              : process.env.NEXT_PUBLIC_IMAGIX_URL?.includes("staging")
              ? "27f785cc-24cb-4531-9b91-4a6c58bfaf37"
              : "771263b2-a630-46f2-ae99-24cece88c4be",
        },
      ],
      data: propertyData,
      listing_id: propertyData?.listing_id,
      property_date_type: propertyData?.property?.property_date_type,
      bank: propertyData?.bank,
      reserve_price: propertyData?.reserve_price,
      asset: propertyData?.property_dump?.asset,
      sub_asset_type: propertyData?.property_dump?.sub_asset_type,
      city: propertyData?.property_dump?.city,
      state: propertyData?.property_dump?.state,
      start_date_time: propertyData?.start_date_time,
      bid_application_form_file_id:
        propertyData.bank?.bid_application_form_file_id?.uuid || null,
      online_kyc_enabled: propertyData.bank?.online_kyc_enabled || false,
    });
    router.replace(
      `/all-events?property_listing_id=${propertyData?.listing_id}&view_detail=true`,
      undefined,
      { shallow: true },
    );
    setOpen(true);
  };

  const columns = useMemo<ColumnDef<tableData>[]>(
    () => [
      // {
      //   id: "select",
      //   header: ({ table }) => <div className="min-w-10">Sr no.</div>,
      //   cell: ({ row }) => {
      //     return <div className="min-w-10">{+row?.id + 1}</div>;
      //   },
      // },
      {
        accessorKey: "listing_id",
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="text-xs px-0 min-w-14 hover:bg-transparent"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Event ID
          </Button>
        ),
        cell: ({ row }) => (
          <div className="min-w-24">{row.getValue("listing_id")}</div>
        ),
      },
      {
        accessorKey: "sub_asset_type",
        header: () => <div className="text-xs min-w-24">Sub Asset Type</div>,
        cell: ({ row }) => {
          return (
            <div className="text-xs min-w-24 capitalize">
              {row?.original?.property_dump?.sub_asset_type?.name || "Na"}
            </div>
          );
        },
      },
      {
        accessorKey: "borrower_name",
        header: () => <div className="min-w-[110px]">Borrower Name</div>,
        cell: ({ row }) => {
          return (
            <div className="font-xs min-w-[110px]">
              {/* {(row.getValue("borrower_name") as string)?.slice(0, 25) || "N/A"} */}
              {(row.getValue("borrower_name") as string)?.length > 23
                ? `${(row.getValue("borrower_name") as string).slice(0, 23)}...`
                : row.getValue("borrower_name") || "N/A"}
            </div>
          );
        },
      },
      {
        accessorKey: "bank_name",
        header: ({ column }) => (
          <div className="capitalize min-w-28">Bank Name</div>
        ),
        cell: ({ row }) => (
          <div className="capitalize min-w-28">
            {row.getValue("bank_name") || "N/A"}
          </div>
        ),
      },
      {
        accessorKey: "final_price",
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="text-xs px-0 h-8 hover:bg-transparent"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Reserve price
          </Button>
        ),
        cell: ({ row }) => {
          const amount = parseFloat(row.getValue("final_price"));
          return (
            <div className="min-w-20 pl-2">₹{amountFormatter(amount)}</div>
          );
        },
      },
      {
        accessorKey: "start_date_time",
        header: ({ table }) => (
          <div className="min-w-[150px]">Auction Start Date & Time</div>
        ),
        cell: ({ row }) => (
          <div className="capitalize text-xs break-keep min-w-[190px]  ">
            {row.getValue<string>("start_date_time") ? (
              <div className="flex items-center divide-x divide-gray-400">
                <div className="pr-2 break-keep">
                  {DateTime.fromISO(row.getValue<string>("start_date_time"))
                    .setZone("Asia/Kolkata")
                    .toFormat("dd MMM yyyy")}
                </div>
                <div className="pl-2 text-xs break-keep">
                  {`${DateTime.fromISO(row.getValue<string>("start_date_time"))
                    .setZone("Asia/Kolkata")
                    .toFormat("hh:mm a")} (IST)`}
                </div>
              </div>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: "tag",
        header: "",
        cell: ({ row }) => {
          const current_date_object = DateTime.now()
            .setZone("Asia/Kolkata")
            .set({
              hour: 0,
              minute: 0,
              second: 0,
              millisecond: 0,
            });
          const emd_last_date_object = DateTime.fromISO(
            row?.original?.property_dump?.emd_last_date,
          ).setZone("Asia/Kolkata");

          const diffDays = Math.floor(
            emd_last_date_object.diff(current_date_object, "days").days,
          );
          return (
            <>
              {diffDays <= 0 && (
                <div className="uppercase min-w-[105px] bg-[#FEECE6] text-brand-orange pl-2 pr-2 py-[2px] clip-path-topRightFourSided text-[10px] mr-2">
                  {"starting soon"}
                </div>
              )}
            </>
          );
        },
      },
      {
        id: "actions",
        header: () => <div className="text-center">Actions</div>,
        cell: ({ row }) => {
          return (
            // <DropdownMenu>
            //   <DropdownMenuTrigger asChild>
            <Button
              // variant="ghost"
              className="h-7 px-3 text-xs"
              onClick={() => viewDetailsClickHandler(row.original)}
            >
              View Details
            </Button>
            //    </DropdownMenuTrigger>
            //   <DropdownMenuContent align="end">
            //     <DropdownMenuLabel>Actions</DropdownMenuLabel>
            //     <DropdownMenuItem
            //       onClick={() => navigator.clipboard.writeText(payment.id)}
            //     >
            //       Copy payment ID
            //     </DropdownMenuItem>
            //     <DropdownMenuSeparator />
            //     <DropdownMenuItem>View customer</DropdownMenuItem>
            //     <DropdownMenuItem>View payment details</DropdownMenuItem>
            //   </DropdownMenuContent>
            // </DropdownMenu>
          );
        },
      },
    ],
    [],
  );

  const FilterLeft = () => {
    // const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    // const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const formatDate = (date: Date | undefined): string => {
      if (!date) return "";
      const day = date.getDate().toString().padStart(2, "0");
      const month = date.toLocaleString("default", { month: "short" });
      const year = date.getFullYear().toString().slice(-2);
      return `${day}/${month}/${year}`;
    };
    const [isStartDateOpen, setIsStartDateOpen] = useState(false);
    const [isEndDateOpen, setIsEndDateOpen] = useState(false);

    const handleStartDateSelect = (date: Date | undefined) => {
      if (date && endDate && date > endDate) {
        alert(
          "The start date cannot be later than the selected end date. Please choose a valid start date.",
        );
        return;
      }
      setStartDate(date);
      setIsStartDateOpen(false);
    };

    const handleEndDateSelect = (date: Date | undefined) => {
      if (date && startDate && date < startDate) {
        alert(
          "The end date cannot be earlier than the selected start date. Please choose a valid end date.",
        );
        return;
      }
      setEndDate(date);
      setIsEndDateOpen(false);
    };

    const validateAndPrepareSearch = useCallback(() => {
      if (startDate && endDate && startDate > endDate) {
        return null;
      }
    }, [startDate, endDate]);

    useEffect(() => {
      validateAndPrepareSearch();
    }, [startDate, endDate, validateAndPrepareSearch]);

    return (
      <div className="flex gap-1 sm:gap-4 pr-2">
        {/* <Dropdown
          dataSource={() => {
            return Promise.resolve(scheduleData);
          }}
          defaultSelected={scheduleData[0]}
          selectIdentifier={false}
          onChange={(value: any) => console.log(value)}
          valueKey="value"
          placeholder="Asset type"
          styles={{
            dropdownItemsClass: "text-brand-borderGrey",
            hoverBgColor: "focus:bg-brand-lightPink",
            hoverTextColor: "focus:text-brand-borderGrey",
            outerContainer:
              "border-[1px] border-brand-borderGrey hover:border-brand-orange rounded-lg text-sm leading-normal font-sans text-brand-textBlack px-4",
          }}
        /> */}
        <Popover onOpenChange={setIsStartDateOpen} open={isStartDateOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="flex gap-2 items-center w-full justify-between text-xs sm:text-sm font-openSans text-brand-textLightGrey px-2 sm:px-4"
            >
              <CalendarDotsIcon width={16} height={16} />
              {startDate ? formatDate(startDate) : <span>Start date</span>}
              <div className="pr-2 pl-2">
                {isStartDateOpen ? (
                  <ChevronUpSvg width={14} height={14} fill={"#7D7D7D"} />
                ) : (
                  <ChevronDownSvg width={14} height={14} fill={"#7D7D7D"} />
                )}
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="font-openSans text-sm md:text-base text-center font-semibold text-brand-textLightGrey px-3">
              Select start date
            </div>
            <Calendar
              mode="single"
              selected={startDate}
              onSelect={handleStartDateSelect}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        <Popover onOpenChange={setIsEndDateOpen} open={isEndDateOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="flex gap-2 items-center w-full justify-between text-xs sm:text-sm font-openSans text-brand-textLightGrey px-2 sm:px-4"
            >
              <CalendarDotsIcon width={16} height={16} />
              {endDate ? formatDate(endDate) : <span>End date</span>}
              <div className="pr-2 pl-2">
                {isEndDateOpen ? (
                  <ChevronUpSvg width={14} height={14} fill={"#7D7D7D"} />
                ) : (
                  <ChevronDownSvg width={14} height={14} fill={"#7D7D7D"} />
                )}
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="pr-4">
            <div className="font-openSans text-base text-center font-semibold text-brand-textLightGrey px-3">
              Select End date
            </div>
            <Calendar
              mode="single"
              selected={endDate}
              onSelect={handleEndDateSelect}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  useEffect(() => {
    const updateWidth = () => {
      if (divRef.current) {
        setWidth(divRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);

    return () => window.removeEventListener("resize", updateWidth);
  }, [window.innerWidth, width]);
  console.log("events:selected ", selected);

  return (
    <>
      <div className="h-[1px] w-full" ref={divRef}></div>
      {onboardingData && onboardingData?.status !== "active" && (
        <OnboardingWarningBanner />
      )}
      <div className={`bg-white border border-brand-borderGreyTwo pr-2 -mt-4`}>
        <div className="mx-auto" style={{ width: `${width - 30}px` }}>
          {loading && events.length === 0 ? (
            <AuctionLoader />
          ) : (
            <CustomBasicDataTable
              data={events}
              columns={columns}
              filterComponent={<FilterLeft />}
              onSearch={(value: string) => {
                setSearch(value);
                setPage(1);
              }}
              searchValue={search}
              totalEvents={totalEvents}
              totalPages={totalPages}
              currentPage={page}
              onPageChange={(newPage: number) => {
                setPage(newPage);
              }}
              isLoading={loading}
            />
          )}
        </div>
        <PropertyDetailsModel
          open={open}
          setOpen={handleSetOpen}
          selected={selected}
        />
      </div>
    </>
  );
}
